import React from 'react';
import {
  Alert, Button, SearchInput, Select, Textarea, phone, useToast,
} from '@parsina/framework';
import { SearchX } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { gql } from '../../lib/api';
import TaskDialog from '../../components/TaskDialog';
import { FALLBACK_COUNTRY } from '../../constants/countryProfile';
import { CREATE_COUNTRY_PROFILE } from './queries';
import { NOTE_MAX, NOTE_MIN } from './EditGroupDialog';
import {
  CountryCode, countryErrorMessage, useCountryName, useKnownRegion,
} from './shared';

const ISO2 = /^[A-Za-z]{2}$/;

/**
 * Folded for SEARCH, never for display: lower case, no Latin diacritics, «ß»
 * spelled out — «turk» finds «Türkiye» — and Persian left as it is. The kit's
 * own `phone.countryMatches` is used where the pinned kit has it.
 */
const fold = (text) => String(text ?? '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\u00df/g, 'ss')
  .toLowerCase().trim();

/**
 * «افزودن کشور» (PM-0243): give a country its FIRST row, as a copy.
 *
 * ── THE COUNTRY IS PICKED, NEVER TYPED ──────────────────────────────────────
 * From the kit's country catalogue (`phone.COUNTRIES`, the list every phone
 * picker and the sign-in screen use), in the panel's language, searchable by
 * name or ISO code, minus every country that already has a row. A code the
 * catalogue does not carry can still be had by typing it: two letters ICU knows
 * as a region are offered as one more choice — the service checks the code is
 * an assigned one.
 *
 * ── A COPY, SO NOTHING MOVES ────────────────────────────────────────────────
 * A country with a row of its own no longer reads `*` — an empty row would
 * switch its SMS and WhatsApp off — so the row starts as a copy of a template:
 * `*` (the default: the country behaves exactly as it does today) or a country
 * that has a row (it then behaves like THAT country, dial code included, which
 * the form says before it is saved). The note is required, as on every write.
 * On success the new country's record opens, ready to be edited.
 */
export default function CreateCountryDialog({
  open, onOpenChange, existing, onCreated,
}) {
  const { t, locale } = useTranslation();
  const toast = useToast();
  const countryName = useCountryName();
  const knownRegion = useKnownRegion();

  const [query, setQuery] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [template, setTemplate] = React.useState(FALLBACK_COUNTRY);
  const [note, setNote] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setQuery('');
    setCountry('');
    setTemplate(FALLBACK_COUNTRY);
    setNote('');
    setError(null);
  }, [open]);

  const taken = React.useMemo(() => new Set((existing || []).map((r) => r.country)), [existing]);

  /** Every catalogue country without a row, by name in the panel's language. */
  const candidates = React.useMemo(() => (phone.COUNTRIES || [])
    .filter((row) => !taken.has(row.iso2))
    .map((row) => ({ code: row.iso2, name: countryName(row.iso2), row }))
    .sort((a, b) => a.name.localeCompare(b.name, locale)), [taken, countryName, locale]);

  const q = fold(query);
  const matches = q
    ? candidates.filter((c) => (phone.countryMatches
      ? phone.countryMatches(c.row, query)
      : false)
      || [c.code, c.name, c.row.en, c.row.fa].some((n) => fold(n).includes(q)))
    : candidates;

  // A code the catalogue does not carry: offered when typed in full and known.
  const typed = query.trim().toUpperCase();
  const extra = ISO2.test(typed) && !taken.has(typed)
    && !candidates.some((c) => c.code === typed) && knownRegion(typed)
    ? { code: typed, name: countryName(typed) }
    : null;
  const choices = extra ? [...matches, extra] : matches;

  const templates = [FALLBACK_COUNTRY, ...(existing || []).map((r) => r.country).filter((c) => c !== FALLBACK_COUNTRY)];
  const trimmed = note.trim();
  const noteOk = trimmed.length >= NOTE_MIN && trimmed.length <= NOTE_MAX;
  const dirty = !!(country || query || note || template !== FALLBACK_COUNTRY);

  const submit = async () => {
    if (!country || !noteOk || pending) return;
    setPending(true);
    setError(null);
    try {
      const res = await gql(CREATE_COUNTRY_PROFILE, { country, template, note: trimmed });
      const created = res?.createCountryProfile?.country || country;
      onOpenChange(false);
      toast.success(t('countries.create.success', { country: countryName(created) }));
      onCreated?.(created);
    } catch (e) {
      // Already there — just made by someone else, or never seen by this list.
      setError(e?.code === 'CONFLICT' ? t('countries.create.conflict') : countryErrorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  const templateName = template === FALLBACK_COUNTRY ? '' : `${countryName(template)} (${template})`;

  return (
    <TaskDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('countries.create.title')}
      error={error}
      pending={pending}
      dirty={dirty}
      size="lg"
      submitLabel={t('countries.create.submit')}
      submitDisabled={!country || !noteOk}
      onSubmit={submit}
      footerNote={!country ? t('countries.create.pickCountry') : (!noteOk ? t('countries.edit.noteNeeded') : undefined)}
      data-testid="country-create"
    >
      <p className="text-sm leading-6 text-muted-foreground">{t('countries.create.body')}</p>

      <fieldset className="flex min-w-0 flex-col gap-2" disabled={pending} data-testid="country-create-picker">
        <legend className="mb-1.5 text-[11.5px] font-semibold text-muted-foreground">{t('countries.create.country')}</legend>
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target?.value ?? e)}
          onKeyDown={(e) => { if (e.key === 'Escape' && query) { e.stopPropagation(); setQuery(''); } }}
          placeholder={t('countries.create.searchPlaceholder')}
          aria-label={t('countries.create.searchLabel')}
          autoFocus
          data-testid="country-create-search"
        />
        {!candidates.length && !extra ? (
          <p className="m-0 py-3 text-sm text-muted-foreground" data-state="empty" data-testid="country-create-empty">
            {t('countries.create.empty')}
          </p>
        ) : !choices.length ? (
          <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground" data-state="empty" data-testid="country-create-no-match">
            <SearchX size={16} aria-hidden />
            <span>{t('countries.create.noMatch')}</span>
            <Button variant="ghost" size="sm" onClick={() => setQuery('')}>{t('countries.create.clearSearch')}</Button>
          </div>
        ) : (
          <div
            role="radiogroup"
            aria-label={t('countries.create.country')}
            className="flex max-h-60 flex-col overflow-y-auto rounded-lg border border-border"
            data-state="content"
            data-testid="country-create-choices"
          >
            {choices.map((c) => (
              <label
                key={c.code}
                className="flex cursor-pointer items-center gap-2.5 border-b border-border px-3 py-2 text-sm last:border-b-0 hover:bg-muted"
                data-testid={`country-create-choice-${c.code}`}
              >
                <input
                  type="radio"
                  name="country-create-choice"
                  value={c.code}
                  checked={country === c.code}
                  onChange={() => setCountry(c.code)}
                  className="h-4 w-4 accent-primary"
                />
                <span className="min-w-0 flex-1 truncate">{c.name}</span>
                <CountryCode code={c.code} />
              </label>
            ))}
          </div>
        )}
      </fieldset>

      <div data-testid="country-create-template">
        <Select
          label={t('countries.create.template')}
          value={template}
          onChange={(e) => setTemplate(e.target?.value ?? e)}
          options={templates.map((code) => ({
            value: code,
            label: code === FALLBACK_COUNTRY ? t('countries.create.templateFallback') : `${countryName(code)} (${code})`,
          }))}
          placeholder={null}
          disabled={pending}
        />
      </div>
      {template === FALLBACK_COUNTRY ? (
        <p className="m-0 text-xs leading-5 text-muted-foreground" data-testid="country-create-same">
          {t('countries.create.sameAsToday')}
        </p>
      ) : (
        // Not an error: what the choice does — the warning tone, never red.
        <Alert variant="warning" data-testid="country-create-copies">
          {t('countries.create.copiesTemplate', { template: templateName })}
        </Alert>
      )}

      <div data-testid="country-create-note">
        <Textarea
          label={t('countries.edit.note')}
          required
          value={note}
          onChange={(e) => setNote(e.target?.value ?? e)}
          placeholder={t('countries.create.notePlaceholder')}
          helperText={t('countries.edit.noteHelper')}
          maxLength={NOTE_MAX}
          showCount
          rows={2}
          disabled={pending}
        />
      </div>
    </TaskDialog>
  );
}
